import React from "react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { stats, teamValues, milestones, testimonials, companyInfo } from "../data/mock";
import { Hammer, ShieldCheck, Lightbulb, MapPin, Quote } from "lucide-react";

const iconMap = { Hammer, ShieldCheck, Lightbulb, MapPin };

const About = () => {
  return (
    <PageLayout>
      <PageHero
        badge="About Us"
        title="25 Years of Wire & Cable Craftsmanship"
        subtitle="Family-owned, Honolulu-based, and trusted across the Pacific to deliver design, engineering and installation done right the first time."
        breadcrumbs={[{ label: "About" }]}
      />

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl shadow-emerald-500/10">
            <img
              src="https://images.unsplash.com/photo-1553413077-190dd305871c"
              alt="Workshop"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0a2e1f]/40 via-transparent to-transparent" />
          </div>
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Our Story
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-5 tracking-tight" style={{ color: "#0a2e1f" }}>
              Built on craftsmanship, grown by trust.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {companyInfo.name} began in {companyInfo.foundedYear} as a one-person
              shop on Oʻahu — stringing wire, terminating cable, and earning a
              reputation for showing up when promised. Today we serve commercial,
              industrial and military customers across the Pacific with the same
              hands-on attention to every connection.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our team blends seasoned electricians, application engineers and
              custom-assembly specialists. Whether you need a single mil-spec run
              or a complete fiber-optic backbone, we plan, source and install with
              precision.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, idx) => (
            <div key={idx} className="text-center bg-white rounded-2xl p-8 border border-slate-100">
              <div
                className="text-4xl md:text-5xl font-extrabold mb-2 bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #10b981 0%, #047857 100%)",
                }}
              >
                {s.value}
              </div>
              <div className="text-sm text-slate-600 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              What We Stand For
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
              Our Core Values
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamValues.map((v, idx) => {
              const Icon = iconMap[v.icon] || Hammer;
              return (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: "#0a2e1f" }}>{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Milestones
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
              Our Journey
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-emerald-200 md:-translate-x-1/2" />
            {milestones.map((m, idx) => (
              <div
                key={idx}
                className={`relative mb-10 md:mb-14 md:flex ${
                  idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <div className="md:w-1/2 pl-12 md:pl-0 md:px-10">
                  <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                    <div className="text-emerald-700 font-bold text-sm mb-1">{m.year}</div>
                    <h3 className="text-lg font-bold mb-1" style={{ color: "#0a2e1f" }}>{m.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{m.description}</p>
                  </div>
                </div>
                <div
                  className="absolute left-4 md:left-1/2 top-3 w-3 h-3 rounded-full md:-translate-x-1/2 ring-4 ring-emerald-100"
                  style={{ backgroundColor: "#10b981" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Client Voices
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
              What partners say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="relative rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
              >
                <Quote size={28} className="text-emerald-500 mb-4" />
                <p className="text-slate-700 leading-relaxed mb-6 italic">“{t.quote}”</p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #047857 100%)",
                    }}
                  >
                    {t.name.split(" ").map(p => p[0]).join("").slice(0,2)}
                  </div>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: "#0a2e1f" }}>{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default About;
